<template>
  <div class="collapse-text-wrapper">
    <!-- 
      隐藏的标准参照物 
      用于精确测量当前字体、缩放比例下的“单行高度”
    -->
    <div ref="measureRef" class="measure-ref">{{ measureChar }}</div>

    <!-- 内容区域 -->
    <div 
      ref="contentRef" 
      class="text-content" 
      :class="{ 'is-expanded': isExpanded, 'is-clamped': !isExpanded && isOverflow }"
    >
      <slot></slot>
    </div>

    <!-- 展开/折叠按钮 -->
    <span 
      v-if="isOverflow" 
      class="toggle-btn" 
      @click.stop="toggle"
    >
      {{ isExpanded ? collapseText : expandText }}
      <img src="@/assets/images/ico_down2@2x.png" class="icon-arrow" :class="{ rotate: isExpanded }">
    </span>
  </div>
</template>

<script>
export default {
  name: 'CollapseText',
  props: {
    // 用于测量的字符，默认用 'M' 或 '中'，宽度较饱满
    measureChar: {
      type: String,
      default: 'M'
    },
    expandText: {
      type: String,
      default: '更多'
    },
    collapseText: {
      type: String,
      default: '收起'
    }
  },
  data() {
    return {
      isExpanded: false,
      isOverflow: false
    };
  },
  mounted() {
    this.initCheck();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.timer);
  },
  methods: {
    initCheck() {
      this.$nextTick(() => {
        setTimeout(this.checkOverflow, 50);
      });
    },
    toggle() {
      this.isExpanded = !this.isExpanded;
    },
    handleResize() {
      this.debounceCheck();
    },
    debounceCheck() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.checkOverflow();
      }, 150);
    },
    checkOverflow() {
      const contentEl = this.$refs.contentRef;
      const measureEl = this.$refs.measureRef;
      
      if (!contentEl || !measureEl) return;

      // 1. 获取“绝对准确”的单行高度
      // measureEl 继承了父级的 font-size, line-height 等样式
      // 且因为没有 clamp 限制，它的 scrollHeight 就是物理单行高度
      const singleLineHeight = measureEl.scrollHeight;

      // 2. 获取内容的实际高度
      // 注意：如果内容处于 is-clamped 状态，contentEl.scrollHeight 依然是完整内容的高度
      // 如果内容处于 is-expanded 状态，scrollHeight 也是完整高度
      const contentHeight = contentEl.scrollHeight;

      // 3. 判断是否溢出
      // 只要内容高度 > 单行高度 + 微小容错(0.1px)，即视为溢出
      // 使用 singleLineHeight * 1.01 作为阈值可以兼容极端的亚像素渲染
      const hasOverflow = contentHeight > (singleLineHeight + 0.1);

      // 4. 状态同步
      if (hasOverflow) {
        if (!this.isOverflow) {
          this.isOverflow = true;
        }
      } else {
        // 【关键】如果不溢出了（比如窗口变宽了），必须重置所有状态
        if (this.isOverflow || this.isExpanded) {
          this.isOverflow = false;
          this.isExpanded = false;
        }
      }
    }
  },
  updated() {
    this.debounceCheck();
  }
};
</script>

<style lang="scss" scoped>
.collapse-text-wrapper {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-width: 0; 
  position: relative;
  
  // 隐藏测量元素，但必须存在于 DOM 中以继承样式
  .measure-ref {
    position: absolute;
    visibility: hidden;
    height: auto;
    width: auto;
    white-space: nowrap;
    float: left;
    // 确保它不受 flex 布局影响，独立测量
    display: block; 
  }
  
  .text-content {
    flex: 1;
    min-width: 0; 
    margin-right: 8px;
    word-break: break-all;
    white-space: normal;
    // 这里的 line-height 会被 measure-ref 继承并用于测量
    line-height: 1.5; 

    /* 折叠状态 */
    &.is-clamped {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      max-height: 1.5em; 
    }

    /* 展开状态 */
    &.is-expanded {
      display: block;
      max-height: none;
    }
  }

  .toggle-btn {
    flex-shrink: 0;
    cursor: pointer;
    // color: #ff5967;
    // font-size: 13px;
    user-select: none;
    display: flex;
    align-items: center;
    padding-left: 8px;
    line-height: 24px;

    font-weight: 400;
    font-size: 16px;
    color: #666666;
    text-align: right;
    font-style: normal;
    
    &:hover {
      opacity: 0.8;
    }

    .icon-arrow {
      display: inline-block;
      margin-left: 8px;
      width: 14px;
      height: 14px;
      transition: transform 0.3s ease;
      
      &.rotate {
        transform: rotate(180deg);
      }
    }
  }
}
</style>